/**
 * Modal de criação/edição de agendamento — visual design system.
 *
 * Compõe Modal + FormField + Input + Button + Badge + Alert + Spinner do
 * DS. Stepper leve usando Badge + linha de progresso. Zero emojis, zero
 * SVG inline, zero classes hardcoded de cor fora de tokens.
 */

import { useEffect, useMemo, useState } from 'react'
import cronstrue from 'cronstrue/i18n'

import { Badge } from '@/design-system/atoms/Badge'
import { Button } from '@/design-system/atoms/Button'
import { Icon, type IconName } from '@/design-system/atoms/Icon'
import { IconButton } from '@/design-system/atoms/IconButton'
import { Input } from '@/design-system/atoms/Input'
import { Spinner } from '@/design-system/atoms/Spinner'
import { Alert } from '@/design-system/molecules/Alert'
import { FormField } from '@/design-system/molecules/FormField'
import { Modal } from '@/design-system/molecules/Modal'
import { cn } from '@/lib/utils'

import userService, { UserListItem } from '../../services/userService'
import type {
  DeliveryChannel,
  RecipientUser,
  Schedule,
  SchedulableTool,
  ScheduledTask,
  ScheduledTaskCreatePayload,
} from '../../types/scheduler'
import ScheduleBuilder from './ScheduleBuilder'

interface Props {
  tools: SchedulableTool[]
  whatsappEnabled: boolean
  editing: ScheduledTask | null
  onClose: () => void
  onSubmit: (payload: ScheduledTaskCreatePayload) => Promise<void>
}

const DEFAULT_SCHEDULE: Schedule = {
  kind: 'cron',
  expression: '30 7 * * 1-5', // seg-sex 07:30
}

const TOOL_ICONS: Record<string, IconName> = {
  list_open_os: 'file-text',
  list_open_hd: 'ticket',
  cross_reference_os_hd: 'pie-chart',
}

function scheduleFromTask(task: ScheduledTask | null): Schedule {
  if (!task) return DEFAULT_SCHEDULE
  if (task.run_at) return { kind: 'once', runAt: task.run_at }
  return { kind: 'cron', expression: task.cron_expression || '30 7 * * 1-5' }
}

const STEPS = [
  { n: 1, title: 'Agendamento', subtitle: 'O que executar' },
  { n: 2, title: 'Recorrência', subtitle: 'Quando executar' },
  { n: 3, title: 'Destinatários', subtitle: 'Quem recebe o resultado' },
] as const

export default function ScheduledTaskModal({
  tools,
  whatsappEnabled,
  editing,
  onClose,
  onSubmit,
}: Props) {
  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState(editing?.name ?? '')
  const [toolName, setToolName] = useState(editing?.tool_name ?? '')
  const [toolParams, setToolParams] = useState<Record<string, unknown>>(
    editing?.tool_params ?? {}
  )
  const [schedule, setSchedule] = useState<Schedule>(() =>
    scheduleFromTask(editing)
  )

  const [users, setUsers] = useState<UserListItem[]>([])
  const [recipients, setRecipients] = useState<
    Map<number, Set<DeliveryChannel>>
  >(() => {
    const map = new Map<number, Set<DeliveryChannel>>()
    editing?.delivery_recipients.users.forEach(u =>
      map.set(u.id, new Set(u.channels))
    )
    return map
  })
  const [userSearch, setUserSearch] = useState('')
  const [loadingUsers, setLoadingUsers] = useState(true)

  const [failureEmails, setFailureEmails] = useState(
    editing?.failure_notification_recipients.join(', ') ?? ''
  )

  const selectedTool = useMemo(
    () => tools.find(t => t.tool_name === toolName) ?? null,
    [tools, toolName]
  )

  useEffect(() => {
    if (!toolName && tools.length > 0) setToolName(tools[0].tool_name)
  }, [tools, toolName])

  useEffect(() => {
    let active = true
    setLoadingUsers(true)
    userService
      .listUsers()
      .then(list => {
        if (active) setUsers(list)
      })
      .finally(() => {
        if (active) setLoadingUsers(false)
      })
    return () => {
      active = false
    }
  }, [])

  const toggleUserChannel = (userId: number, channel: DeliveryChannel) =>
    setRecipients(prev => {
      const next = new Map(prev)
      const current = new Set(next.get(userId) ?? [])
      if (current.has(channel)) current.delete(channel)
      else current.add(channel)
      if (current.size === 0) next.delete(userId)
      else next.set(userId, current)
      return next
    })

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase()
    if (!q) return users
    return users.filter(
      u =>
        u.full_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    )
  }, [users, userSearch])

  const selectedCount = recipients.size
  const emailCount = Array.from(recipients.values()).filter(s =>
    s.has('email')
  ).length
  const whatsappCount = Array.from(recipients.values()).filter(s =>
    s.has('whatsapp')
  ).length

  const parseList = (value: string) =>
    value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)

  const canGoNext = useMemo(() => {
    if (step === 1) return name.trim() && toolName
    if (step === 2) {
      if (schedule.kind === 'cron') {
        const expr = schedule.expression.trim()
        if (expr.split(/\s+/).length !== 5) return false
        try {
          cronstrue.toString(expr, { locale: 'pt_BR' })
          return true
        } catch {
          return false
        }
      }
      const d = new Date(schedule.runAt)
      return !Number.isNaN(d.getTime()) && d.getTime() > Date.now()
    }
    if (step === 3) return selectedCount > 0
    return false
  }, [step, name, toolName, schedule, selectedCount])

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const usersPayload: RecipientUser[] = Array.from(
        recipients.entries()
      ).map(([id, channels]) => ({ id, channels: Array.from(channels) }))
      const channelsSet = new Set(usersPayload.flatMap(u => u.channels))
      const assignedTo =
        editing?.assigned_to ?? usersPayload[0]?.id ?? users[0]?.id
      if (!assignedTo) {
        setError(
          'Selecione pelo menos um usuário para receber a sessão de chat.'
        )
        setSubmitting(false)
        return
      }
      const payload: ScheduledTaskCreatePayload = {
        name,
        assigned_to: assignedTo,
        tool_name: toolName,
        tool_params: toolParams,
        cron_expression: schedule.kind === 'cron' ? schedule.expression : '',
        run_at: schedule.kind === 'once' ? schedule.runAt : null,
        delivery_channels: Array.from(channelsSet) as DeliveryChannel[],
        delivery_recipients: { users: usersPayload },
        failure_notification_recipients: parseList(failureEmails),
      }
      await onSubmit(payload)
    } catch (err) {
      console.error('[Scheduler] Submit failed', err)
      setError(
        'Não foi possível salvar. Verifique os campos e tente novamente.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const title = editing ? 'Editar agendamento' : 'Novo agendamento'
  const currentStep = STEPS[step - 1]

  const footer = (
    <div className="flex w-full items-center justify-between">
      <Button
        variant="ghost"
        onClick={() => (step > 1 ? setStep(step - 1) : onClose())}
        leftIcon={step > 1 ? <Icon name="arrow-left" size={16} /> : undefined}
      >
        {step > 1 ? 'Voltar' : 'Cancelar'}
      </Button>
      {step < 3 ? (
        <Button
          variant="primary"
          disabled={!canGoNext}
          onClick={() => setStep(step + 1)}
          rightIcon={<Icon name="arrow-right" size={16} />}
        >
          Próximo
        </Button>
      ) : (
        <Button
          variant="primary"
          disabled={submitting || !canGoNext}
          onClick={() => void handleSubmit()}
          leftIcon={
            submitting ? <Spinner size="sm" /> : <Icon name="check" size={16} />
          }
        >
          {submitting
            ? 'Salvando...'
            : editing
              ? 'Salvar alterações'
              : 'Criar agendamento'}
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="xl"
      title={
        <div>
          <h2 className="text-xl font-semibold leading-tight">{title}</h2>
          <p className="mt-0.5 text-sm font-normal text-gray-500 dark:text-gray-400">
            {currentStep.subtitle}
          </p>
        </div>
      }
      footer={footer}
    >
      <div className="flex flex-col gap-6">
        {/* Stepper */}
        <Stepper currentStep={step} />

        {/* Body do passo */}
        <div className="min-h-[280px]">
          {step === 1 && (
            <StepTask
              name={name}
              setName={setName}
              tools={tools}
              toolName={toolName}
              setToolName={setToolName}
              toolParams={toolParams}
              setToolParams={setToolParams}
              selectedTool={selectedTool}
            />
          )}
          {step === 2 && (
            <ScheduleBuilder value={schedule} onChange={setSchedule} />
          )}
          {step === 3 && (
            <StepRecipients
              selectedCount={selectedCount}
              emailCount={emailCount}
              whatsappCount={whatsappCount}
              whatsappEnabled={whatsappEnabled}
              userSearch={userSearch}
              setUserSearch={setUserSearch}
              loadingUsers={loadingUsers}
              filteredUsers={filteredUsers}
              recipients={recipients}
              toggleUserChannel={toggleUserChannel}
              failureEmails={failureEmails}
              setFailureEmails={setFailureEmails}
            />
          )}
        </div>

        {error && <Alert variant="error">{error}</Alert>}
      </div>
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/* Subcomponentes de cada passo                                               */
/* -------------------------------------------------------------------------- */

function Stepper({ currentStep }: { currentStep: number }) {
  return (
    <ol className="flex items-center gap-2" aria-label="Progresso">
      {STEPS.map((s, idx) => {
        const active = s.n === currentStep
        const done = s.n < currentStep
        return (
          <li key={s.n} className="flex flex-1 items-center gap-2">
            <div
              className={cn(
                'flex min-w-0 flex-1 items-center gap-2 rounded-lg px-3 py-2 transition-colors',
                active
                  ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:ring-primary-800'
                  : done
                    ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                    : 'bg-gray-50 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400'
              )}
            >
              <span
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                  active
                    ? 'bg-primary-600 text-white'
                    : done
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                )}
              >
                {done ? <Icon name="check" size={12} /> : s.n}
              </span>
              <span className="hidden truncate text-xs font-medium sm:inline">
                {s.title}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <Icon
                name="chevron-right"
                size={14}
                className="hidden text-gray-300 dark:text-gray-700 sm:inline"
              />
            )}
          </li>
        )
      })}
    </ol>
  )
}

function StepTask({
  name,
  setName,
  tools,
  toolName,
  setToolName,
  toolParams,
  setToolParams,
  selectedTool,
}: {
  name: string
  setName: (v: string) => void
  tools: SchedulableTool[]
  toolName: string
  setToolName: (v: string) => void
  toolParams: Record<string, unknown>
  setToolParams: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
  selectedTool: SchedulableTool | null
}) {
  return (
    <div className="space-y-5">
      <FormField label="Nome do agendamento" htmlFor="task-name" required>
        <Input
          id="task-name"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Ex: Diário de HDs em aberto"
        />
      </FormField>

      <FormField label="Tool a executar" required>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {tools.map(t => {
            const active = t.tool_name === toolName
            const icon = TOOL_ICONS[t.tool_name] ?? 'settings'
            return (
              <button
                key={t.tool_name}
                type="button"
                onClick={() => {
                  setToolName(t.tool_name)
                  setToolParams({})
                }}
                aria-pressed={active}
                className={cn(
                  'flex flex-col gap-2 rounded-xl border p-3 text-left transition-all',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                  active
                    ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500 dark:bg-primary-900/20'
                    : 'border-gray-200 hover:border-primary-300 dark:border-gray-700 dark:hover:border-primary-700'
                )}
              >
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-lg',
                    active
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                  )}
                >
                  <Icon name={icon} size={16} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t.display_name}
                  </p>
                  <p className="line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
                    {t.description}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      </FormField>

      {selectedTool?.configurable_params?.map(param => (
        <FormField key={param.name} label={param.label}>
          {param.type === 'preset' && param.options ? (
            <div className="flex flex-wrap gap-2">
              {param.options.map(opt => {
                const current = toolParams[param.name] ?? param.default
                const active = String(current) === String(opt.value)
                return (
                  <Button
                    key={String(opt.value)}
                    type="button"
                    size="sm"
                    variant={active ? 'primary' : 'secondary'}
                    onClick={() =>
                      setToolParams(prev => ({
                        ...prev,
                        [param.name]: opt.value,
                      }))
                    }
                  >
                    {opt.label}
                  </Button>
                )
              })}
            </div>
          ) : (
            <Input
              value={String(toolParams[param.name] ?? '')}
              onChange={e =>
                setToolParams(prev => ({
                  ...prev,
                  [param.name]: e.target.value,
                }))
              }
            />
          )}
        </FormField>
      ))}
    </div>
  )
}

function StepRecipients({
  selectedCount,
  emailCount,
  whatsappCount,
  whatsappEnabled,
  userSearch,
  setUserSearch,
  loadingUsers,
  filteredUsers,
  recipients,
  toggleUserChannel,
  failureEmails,
  setFailureEmails,
}: {
  selectedCount: number
  emailCount: number
  whatsappCount: number
  whatsappEnabled: boolean
  userSearch: string
  setUserSearch: (v: string) => void
  loadingUsers: boolean
  filteredUsers: UserListItem[]
  recipients: Map<number, Set<DeliveryChannel>>
  toggleUserChannel: (userId: number, channel: DeliveryChannel) => void
  failureEmails: string
  setFailureEmails: (v: string) => void
}) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        <Badge variant="primary" size="sm" shape="pill">
          <Icon name="users" size={12} className="mr-1" />
          {selectedCount} usuário{selectedCount !== 1 ? 's' : ''}
        </Badge>
        <Badge variant="info" size="sm" shape="pill">
          <Icon name="mail" size={12} className="mr-1" />
          {emailCount} e-mail
        </Badge>
        {whatsappEnabled && (
          <Badge variant="success" size="sm" shape="pill">
            <Icon name="chat" size={12} className="mr-1" />
            {whatsappCount} WhatsApp
          </Badge>
        )}
      </div>

      <FormField label="Buscar usuário" htmlFor="user-search">
        <Input
          id="user-search"
          value={userSearch}
          onChange={e => setUserSearch(e.target.value)}
          placeholder="Nome ou e-mail"
          leftIcon={<Icon name="search" size={16} />}
        />
      </FormField>

      <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white dark:divide-gray-800 dark:border-gray-700 dark:bg-gray-900/50">
        {loadingUsers && (
          <div className="flex items-center justify-center gap-2 p-6 text-sm text-gray-500">
            <Spinner size="sm" />
            Carregando...
          </div>
        )}
        {!loadingUsers && filteredUsers.length === 0 && (
          <p className="p-6 text-center text-sm text-gray-500">
            Nenhum usuário encontrado.
          </p>
        )}
        {filteredUsers.map(user => {
          const selected = recipients.get(user.id) ?? new Set<DeliveryChannel>()
          const hasPhone = Boolean(user.phone)
          const isSelected = selected.size > 0
          return (
            <div
              key={user.id}
              className={cn(
                'flex items-center gap-3 p-3 transition-colors',
                isSelected
                  ? 'bg-primary-50/60 dark:bg-primary-900/10'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
              )}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-600 text-sm font-semibold text-white">
                {user.full_name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                  {user.full_name}
                </p>
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {user.email}
                  {hasPhone && (
                    <span className="text-gray-400 dark:text-gray-600">
                      {' '}
                      · {user.phone}
                    </span>
                  )}
                </p>
              </div>
              <ChannelToggle
                label="E-mail"
                iconName="mail"
                active={selected.has('email')}
                onToggle={() => toggleUserChannel(user.id, 'email')}
                disabled={!user.email}
                disabledHint="Usuário sem e-mail cadastrado"
              />
              {whatsappEnabled && (
                <ChannelToggle
                  label="WhatsApp"
                  iconName="chat"
                  active={selected.has('whatsapp')}
                  onToggle={() => toggleUserChannel(user.id, 'whatsapp')}
                  disabled={!hasPhone}
                  disabledHint="Cadastre WhatsApp no perfil"
                />
              )}
            </div>
          )
        })}
      </div>

      <FormField
        label="E-mails para notificação de falha"
        htmlFor="failure-emails"
        hint="Opcional — separados por vírgula. Padrão: superusers."
      >
        <Input
          id="failure-emails"
          value={failureEmails}
          onChange={e => setFailureEmails(e.target.value)}
          placeholder="oncall@empresa.com, sre@empresa.com"
        />
      </FormField>
    </div>
  )
}

function ChannelToggle({
  label,
  iconName,
  active,
  onToggle,
  disabled,
  disabledHint,
}: {
  label: string
  iconName: IconName
  active: boolean
  onToggle: () => void
  disabled?: boolean
  disabledHint?: string
}) {
  return (
    <IconButton
      variant={active ? 'primary' : 'ghost'}
      size="sm"
      disabled={disabled}
      onClick={onToggle}
      title={disabled ? disabledHint : label}
      aria-label={label}
      aria-pressed={active}
    >
      <Icon name={iconName} size={14} />
    </IconButton>
  )
}
